import { useState, useCallback } from 'react';

import { generatePath, useNavigate } from 'react-router-dom';

import { useMutation } from '@zenbusiness/application-commons-graphql';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import { useVisit } from '@zenbusiness/application-commons-visit';

import { THREAD_MODES } from '#client/components/utils/constants';
import * as ROUTES from '#client/constants/routes';
import { useUnifiedBusinessEntityContext } from '#client/contexts/UnifiedBusinessEntityProvider';
import { CREATE_THREAD_MUTATION } from '#client/mutations/createThreadMutation';
import { buildThreadIdentity } from '#client/utils/buildThreadIdentity';

import useUserMode from './useUserMode';

/**
 * Custom hook for thread creation
 *
 * Handles:
 * - Thread creation via GraphQL mutation
 * - Loading states
 * - Error handling with retry logic
 * - Automatic redirect to thread viewer
 * - Thread list updates via centralized ThreadsContext
 */

export function useThreadCreation() {
  const [retryCount, setRetryCount] = useState(0);
  const [customError, setCustomError] = useState(null);

  const navigate = useNavigate();
  const { businessEntityUuid, updateBusinessEntityUuid, updateBusinessEntityUuidFromData } =
    useUnifiedBusinessEntityContext();

  // Multi-mode support
  const userMode = useUserMode();
  const { visitUuid } = useVisit();

  // Initialize captureException with static tags for better performance
  const captureException = useCaptureException({
    tags: {
      frontend: 'assistant-ui',
      operationName: 'createThread'
    }
  });

  const [createThreadMutation, { loading, error }] = useMutation(CREATE_THREAD_MUTATION, {
    onSuccess: async (result) => {
      // Thread creation successful, data will be processed in createNewThread
      console.log('Got thread data on creation --- result', result);
    },
    onError: (err) => {
      const isNetworkError =
        err.message?.includes('fetch') || err.message?.includes('Network') || err.networkError;

      captureException(err, {
        tags: {
          errorType: isNetworkError ? 'network_error' : 'graphql_mutation',
          userMode: userMode.mode,
          businessEntityUuid: businessEntityUuid || 'unknown'
        },
        contexts: {
          threadCreation: {
            retryCount,
            userMode: userMode.mode,
            businessEntityUuid,
            userUuid: userMode.userUuid,
            visitUuid,
            isNetworkError,
            wasLoading: loading
          },
          mutationError: {
            message: err.message,
            networkError: err.networkError,
            graphQLErrors: err.graphQLErrors
          }
        }
      });
    }
  });

  /**
   * Creates a new thread and redirects to thread viewer
   * @param {string} overrideBusinessEntityUuid - Optional override for business entity UUID
   * @param {object} initialInput - Optional initial input to override default greeting
   * @returns {Promise<{threadUuid: string, messages: Array}>}
   */
  const createNewThread = useCallback(
    async (overrideBusinessEntityUuid = null, initialInput = null) => {
      const entityUuid = overrideBusinessEntityUuid || businessEntityUuid;

      // Clear any previous custom errors
      setCustomError(null);

      // Mode-aware validation: Check required UUIDs based on current mode
      let validationError = null;

      switch (userMode.mode) {
        case THREAD_MODES.ANONYMOUS:
        case THREAD_MODES.STANDALONE:
          if (!visitUuid) {
            validationError = new Error(`Visit UUID is required for ${userMode.mode} mode`);
          }
          break;

        case THREAD_MODES.STARTER:
          if (!userMode.userUuid) {
            validationError = new Error('User UUID is required for starter mode');
          }
          break;

        case THREAD_MODES.BUSINESS:
          if (!entityUuid || !userMode.userUuid) {
            validationError = new Error(
              'Business entity UUID and user UUID are required for business mode'
            );
          }
          break;

        default:
          validationError = new Error(`Unknown user mode: ${userMode.mode}`);
      }

      if (validationError) {
        captureException(validationError, {
          tags: {
            errorType: 'validation_error',
            userMode: userMode.mode,
            businessEntityUuid: entityUuid || 'unknown'
          },
          contexts: {
            threadCreation: {
              userMode: userMode.mode,
              businessEntityUuid: entityUuid || 'unknown',
              userUuid: userMode.userUuid || 'unknown',
              visitUuid: visitUuid || 'unknown',
              hasEntityUuid: !!entityUuid,
              hasUserUuid: !!userMode.userUuid,
              hasVisitUuid: !!visitUuid
            }
          }
        });
        setCustomError(validationError);
        throw validationError;
      }

      try {
        // Update the context with the UUID we're using for thread creation
        // Only for business mode - other modes don't have business entities
        if (userMode.mode === THREAD_MODES.BUSINESS && entityUuid) {
          updateBusinessEntityUuid(entityUuid);
        }

        // Create default input or use provided initial input
        const defaultInput = {
          businessEntityUuid: entityUuid,
          messageType: 'greet',
          message: 'greet',
          role: 'user'
        };

        const input = initialInput || defaultInput;

        // Build mode-aware identity for GraphQL mutation using shared utility
        const { identity, threadMode } = buildThreadIdentity(
          userMode.mode,
          visitUuid,
          userMode.userUuid,
          entityUuid
        );

        const result = await createThreadMutation({
          variables: {
            identity,
            input,
            threadMode
          }
        });

        const threadData = result?.data?.createThread;
        const newThreadUuid = threadData?.threadUuid;

        // Check if the response contains an error (like rate limiting)
        if (threadData?.error) {
          const responseError = new Error(threadData.error.message);
          responseError.code = threadData.error.code;
          responseError.statusCode = threadData.error.statusCode;
          responseError.data = { createThread: threadData }; // Include the full response for error checking

          captureException(responseError, {
            tags: {
              errorType:
                threadData.error.code === 'RATE_LIMITED' ? 'rate_limited' : 'response_error',
              userMode: userMode.mode,
              businessEntityUuid: entityUuid,
              errorCode: threadData.error.code
            },
            contexts: {
              threadCreation: {
                userMode: userMode.mode,
                threadMode,
                businessEntityUuid: entityUuid,
                userUuid: userMode.userUuid,
                visitUuid,
                retryCount,
                responseError: threadData.error,
                statusCode: threadData.error.statusCode
              }
            }
          });

          setCustomError(responseError);
          throw responseError;
        }

        if (!newThreadUuid) {
          const error1 = new Error('Thread creation failed: No thread UUID returned');
          captureException(error1, {
            tags: {
              errorType: 'invalid_response',
              userMode: userMode.mode,
              businessEntityUuid: entityUuid
            },
            contexts: {
              threadCreation: {
                userMode: userMode.mode,
                threadMode,
                businessEntityUuid: entityUuid,
                userUuid: userMode.userUuid,
                visitUuid,
                retryCount,
                responseData: threadData,
                hasResult: !!result,
                hasData: !!result?.data,
                hasCreateThread: !!result?.data?.createThread
              }
            }
          });
          throw error1; // Fixed: was 'error', now 'error1'
        }

        // Extract and update business entity UUID from GraphQL response
        // Only for business mode - other modes don't have business entities
        if (userMode.mode === THREAD_MODES.BUSINESS) {
          updateBusinessEntityUuidFromData(threadData?.data || []);
        }

        // Reset retry count on success
        setRetryCount(0);

        // Redirect to thread viewer with navigation state
        navigate(generatePath(ROUTES.thread, { threadUuid: newThreadUuid }), {
          replace: true,
          state: {
            fromThreadCreation: true,
            userMode: userMode.mode,
            threadMode,
            businessEntityUuid: entityUuid,
            visitUuid,
            isInitialLoad: true,
            threadData: {
              threadUuid: newThreadUuid,
              createdAt: new Date().toISOString()
            }
          }
        });

        return {
          threadUuid: newThreadUuid,
          data: threadData.data || []
        };
      } catch (err) {
        const isNetworkError =
          err.message?.includes('fetch') || err.message?.includes('Network') || err.networkError;

        captureException(err, {
          tags: {
            errorType: isNetworkError ? 'network_error' : 'thread_creation_failed',
            userMode: userMode.mode,
            businessEntityUuid: entityUuid || 'unknown'
          },
          contexts: {
            threadCreation: {
              userMode: userMode.mode,
              businessEntityUuid: entityUuid,
              userUuid: userMode.userUuid,
              visitUuid,
              retryCount,
              errorMessage: err.message,
              isNetworkError
            }
          }
        });

        setCustomError(err);
        throw err;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      error,
      businessEntityUuid,
      userMode.userUuid,
      visitUuid,
      userMode.mode,
      createThreadMutation,
      navigate,
      updateBusinessEntityUuid,
      updateBusinessEntityUuidFromData,
      retryCount
      // captureException
      // refetchThreads
    ]
  );

  /**
   * Retry thread creation with exponential backoff
   */
  const retryThreadCreation = useCallback(
    async (overrideBusinessEntityUuid = null, initialInput = null) => {
      const newRetryCount = retryCount + 1;
      setRetryCount(newRetryCount);

      // Exponential backoff: 1s, 2s, 4s, 8s, max 8s
      const delay = Math.min(1000 * 2 ** (newRetryCount - 1), 8000);

      // Add delay before retrying to implement exponential backoff
      await new Promise((resolve) => {
        setTimeout(resolve, delay);
      });
      return createNewThread(overrideBusinessEntityUuid, initialInput);
    },
    [retryCount, createNewThread]
  );

  /**
   * Reset error state
   */
  const clearError = useCallback(() => {
    setRetryCount(0);
  }, []);

  return {
    // State
    loading,
    error,
    retryCount,
    customError,

    // Actions
    createNewThread,
    retryThreadCreation,
    clearError,

    // Computed values
    canRetry: retryCount < 3 && !loading, // Max 3 retries
    isNetworkError: Boolean(
      error?.message?.includes('fetch') ||
        error?.message?.includes('Network') ||
        error?.networkError
    ),
    isAuthError: Boolean(
      error?.message?.includes('401') || error?.message?.includes('authentication')
    )
  };
}
